/**
 * "Ver notificações" (spec 037): o histórico do sino, com paginação, filtro por
 * tipo e por lido/não lido — o que a pessoa tem no sino, sem o limite das 20
 * linhas. O filtro e a página vivem na URL; o lido é do servidor, como no sino.
 */
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCheck, Settings } from 'lucide-react';
import {
  NOTIFICATION_EVENT_KINDS,
  NOTIFICATION_READ_STATES,
  NOTIFICATION_TYPE_LABELS,
  unreadBadge,
  type NotificationItem,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Label, Select } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { pageCountOf, Pagination } from '@/components/ui/pagination';
import { BellRow } from '@/shared/notifications-bell';
import {
  NOTIFICATIONS_PAGE_SIZE,
  NotificationsUnavailable,
  notificationsPageQuery,
  useNotificationMarks,
} from './notifications-queries';

// validateSearch roda no router — puro
export const notificationsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1).catch(1),
  type: z.enum(NOTIFICATION_EVENT_KINDS).optional().catch(undefined),
  state: z.enum(NOTIFICATION_READ_STATES).default('all').catch('all'),
});
export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;

const STATE_LABELS: Record<(typeof NOTIFICATION_READ_STATES)[number], string> = {
  all: 'Todas',
  unread: 'Não lidas',
  read: 'Lidas',
};

export function NotificationsPage() {
  const search = useSearch({ from: '/_authenticated/notificacoes' });
  const navigate = useNavigate();
  const pageQ = useQuery(notificationsPageQuery(search));
  const { readOne, readAll } = useNotificationMarks();
  const unavailable = pageQ.error instanceof NotificationsUnavailable;

  const go = (next: Partial<NotificationsSearch>) =>
    void navigate({ to: '/notificacoes', search: { ...search, ...next }, replace: true });

  const data = pageQ.data;
  const pageCount = pageCountOf(data?.total ?? 0, NOTIFICATIONS_PAGE_SIZE);
  const openItem = (item: NotificationItem) => {
    if (!item.read) readOne.mutate(item.key);
    void navigate({ to: '/tickets/$ticketNumber', params: { ticketNumber: String(item.ticketNumber) } });
  };

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Notificações</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            O que aconteceu nos tickets que você acompanha. Lido e não lido seguem você em qualquer máquina.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            data-tour="notificacoes-marcar-lidas"
            disabled={!data || data.unread === 0 || readAll.isPending}
            onClick={() => readAll.mutate(undefined)}
          >
            <CheckCheck className="size-4" aria-hidden />
            Marcar como lidas{data && data.unread > 0 ? ` (${unreadBadge(data.unread)})` : ''}
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <Link to="/account" search={{ tab: 'notificacoes' }}>
              <Settings className="size-4" aria-hidden />
              Configurações
            </Link>
          </Button>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3" data-tour="notificacoes-filtros">
        <div className="flex flex-col gap-1">
          <Label htmlFor="notif-type">Tipo</Label>
          <Select
            id="notif-type"
            value={search.type ?? ''}
            onChange={(e) =>
              go({ page: 1, type: (e.target.value || undefined) as NotificationsSearch['type'] })
            }
          >
            <option value="">Todos os tipos</option>
            {NOTIFICATION_EVENT_KINDS.map((key) => (
              <option key={key} value={key}>
                {NOTIFICATION_TYPE_LABELS[key]}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="notif-state">Mostrar</Label>
          <Select
            id="notif-state"
            value={search.state}
            onChange={(e) => go({ page: 1, state: e.target.value as NotificationsSearch['state'] })}
          >
            {NOTIFICATION_READ_STATES.map((s) => (
              <option key={s} value={s}>
                {STATE_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>
        {data?.total != null ? (
          <span className="ml-auto pb-2 text-[13px] text-muted-foreground tabular-nums">
            {data.total} {data.total === 1 ? 'notificação' : 'notificações'}
          </span>
        ) : null}
      </div>

      <section className="rounded-lg border bg-card shadow-xs">
        {pageQ.isPending ? (
          <div className="space-y-2 p-3" aria-busy>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : unavailable ? (
          <EmptyState message="As notificações estão sendo atualizadas. Tente de novo em alguns minutos." />
        ) : pageQ.isError ? (
          <EmptyState message="Erro ao carregar as notificações." />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            message={
              search.state === 'unread' ? 'Nenhuma notificação não lida.' : 'Nenhuma notificação com este filtro.'
            }
          />
        ) : (
          <ul className="divide-y divide-divider">
            {data.items.map((item) => (
              <BellRow key={item.key} item={item} onOpen={() => openItem(item)} />
            ))}
          </ul>
        )}
      </section>

      {data && pageCount > 1 ? (
        <div className="mt-3 flex justify-end">
          <Pagination page={search.page} pageCount={pageCount} onPage={(page) => go({ page })} />
        </div>
      ) : null}
    </div>
  );
}
